'use client'

import { motion } from 'framer-motion'
import { ShieldCheck, Lock, Bug, Code2, Database, Clock, Mail, CheckCircle2, AlertTriangle, Eye } from 'lucide-react'
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.9, ease: EASE_CUSTOM },
}

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.8, delay: i * 0.08, ease: EASE_CUSTOM },
})

const technicalMeasures = [
  {
    icon: Lock,
    title: 'Cifrado en tránsito (HTTPS/TLS)',
    desc: 'Todas las comunicaciones entre el cliente y nuestro servidor se realizan mediante conexiones cifradas con TLS 1.2 o superior. Los certificados SSL/TLS están gestionados y renovados automáticamente.',
    color: 'text-blue-600',
    bg: 'from-blue-50 to-indigo-50',
  },
  {
    icon: ShieldCheck,
    title: 'Accesos restringidos y control de permisos',
    desc: 'Aplicamos el principio de mínimo privilegio en todos los sistemas. Los accesos a entornos de producción están protegidos por autenticación multifactor (MFA) y claves SSH. No se utilizan contraseñas predeterminadas.',
    color: 'text-indigo-600',
    bg: 'from-indigo-50 to-violet-50',
  },
  {
    icon: Database,
    title: 'Protección de datos almacenados',
    desc: 'Los datos sensibles almacenados en bases de datos están cifrados en reposo. Se realizan copias de seguridad periódicas con verificación de integridad. El acceso directo a bases de datos de producción está restringido a personal autorizado.',
    color: 'text-violet-600',
    bg: 'from-violet-50 to-purple-50',
  },
  {
    icon: Eye,
    title: 'Monitorización y registros de auditoría',
    desc: 'Mantenemos registros de auditoría (logs) de accesos y operaciones críticas. Los sistemas están monitorizados para detectar actividades inusuales o intentos de acceso no autorizado.',
    color: 'text-emerald-600',
    bg: 'from-emerald-50 to-teal-50',
  },
]

const devPractices = [
  {
    title: 'OWASP Top 10',
    desc: 'Nuestros desarrollos tienen en cuenta el OWASP Top 10 de vulnerabilidades de seguridad en aplicaciones web: inyecciones SQL, XSS, autenticación rota, exposición de datos sensibles, etc.',
  },
  {
    title: 'Revisión de código',
    desc: 'Todo el código pasa por un proceso de revisión antes de su despliegue en producción, incluyendo análisis de posibles vulnerabilidades de seguridad.',
  },
  {
    title: 'Gestión de dependencias',
    desc: 'Las dependencias de terceros se auditan regularmente para detectar vulnerabilidades conocidas (CVEs). Utilizamos herramientas automatizadas de escaneo de dependencias.',
  },
  {
    title: 'Entornos separados',
    desc: 'Los entornos de desarrollo, pruebas y producción están completamente separados. Los datos reales de producción nunca se utilizan en entornos de desarrollo.',
  },
  {
    title: 'Variables de entorno',
    desc: 'Las credenciales, claves API y secretos nunca se almacenan en el código fuente. Se gestionan mediante variables de entorno y servicios de gestión de secretos.',
  },
  {
    title: 'Actualizaciones de seguridad',
    desc: 'Mantenemos los sistemas operativos, servidores y dependencias actualizados con los últimos parches de seguridad de forma periódica.',
  },
]

const retentionPolicies = [
  { category: 'Datos de contacto de clientes', retention: '2 años desde el último contacto', deletion: 'Supresión automática o a petición' },
  { category: 'Registros de auditoría (logs)', retention: '12 meses', deletion: 'Rotación automática' },
  { category: 'Datos de proyectos finalizados', retention: 'Duración del proyecto + 5 años', deletion: 'Conforme a obligaciones legales' },
  { category: 'Copias de seguridad', retention: '30 días (incrementales) / 90 días (completas)', deletion: 'Sobreescritura automática' },
]

const reportSteps = [
  { step: '01', title: 'Notificación responsable', desc: 'Envíe un correo a info@innovatheratech.com con el asunto "VULNERABILIDAD SEGURIDAD" describiendo el problema de forma detallada.' },
  { step: '02', title: 'Acuse de recibo', desc: 'Confirmaremos la recepción de su reporte en un plazo máximo de 48 horas laborables.' },
  { step: '03', title: 'Evaluación y resolución', desc: 'Evaluaremos la vulnerabilidad reportada y trabajaremos en una solución. Le mantendremos informado del progreso.' },
  { step: '04', title: 'Divulgación coordinada', desc: 'Una vez resuelta la vulnerabilidad, coordinaremos con usted los términos y plazos de divulgación pública si fuera apropiado.' },
]

export default function SegurridadClient() {
  return (
    <section className="pt-28 pb-20 relative z-10">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <motion.div {...fadeUp} className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 mb-5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-xs font-mono tracking-[0.25em] text-emerald-600 uppercase">Seguridad & confianza</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
            Compromiso con la{' '}
            <span className="text-blue-600">Seguridad</span>
          </h1>
          <p className="text-slate-500 text-lg font-light max-w-2xl mx-auto leading-relaxed">
            La seguridad no es una característica opcional: es un principio fundamental en todo lo que construimos. Conozca cómo protegemos los datos e infraestructuras de nuestros clientes.
          </p>
          <p className="text-xs font-mono text-slate-400 mt-4 tracking-wider uppercase">
            Última actualización: Mayo 2026
          </p>
        </motion.div>

        {/* Incident history banner */}
        <motion.div
          {...stagger(0)}
          className="glass-card rounded-2xl p-5 mb-6 flex items-center gap-4"
        >
          <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="font-bold text-slate-900 text-sm">Historial de incidentes de seguridad</p>
            <p className="text-slate-500 text-sm">
              <span className="font-semibold text-emerald-600">Ningún incidente de seguridad registrado</span> a la fecha de la última actualización de esta página (Marzo 2026).
            </p>
          </div>
        </motion.div>

        {/* Technical measures */}
        <motion.div {...stagger(1)} className="mb-6">
          <div className="glass-card rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                <Lock className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 leading-snug pt-1.5">Medidas técnicas de seguridad</h2>
            </div>
            <div className="pl-14 grid grid-cols-1 md:grid-cols-2 gap-4">
              {technicalMeasures.map((m) => (
                <div key={m.title} className="bg-white/60 rounded-xl p-4 border border-slate-200/30">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${m.bg} flex items-center justify-center mb-3`}>
                    <m.icon className={`w-4 h-4 ${m.color}`} />
                  </div>
                  <p className="font-semibold text-slate-800 text-sm mb-1.5">{m.title}</p>
                  <p className="text-slate-500 text-xs leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Secure development practices */}
        <motion.div {...stagger(2)} className="mb-6">
          <div className="glass-card rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center">
                <Code2 className="w-5 h-5 text-violet-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 leading-snug pt-1.5">Prácticas de desarrollo seguro</h2>
            </div>
            <div className="pl-14 grid grid-cols-1 md:grid-cols-2 gap-3">
              {devPractices.map((p) => (
                <div key={p.title} className="bg-white/60 rounded-xl p-3.5 border border-slate-200/30">
                  <p className="font-semibold text-slate-800 text-sm mb-1">{p.title}</p>
                  <p className="text-slate-500 text-xs leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Vulnerability reporting */}
        <motion.div {...stagger(3)} className="mb-6">
          <div className="glass-card rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
                <Bug className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900 leading-snug">Reporte responsable de vulnerabilidades</h2>
                <p className="text-slate-500 text-sm mt-1">Si descubres una vulnerabilidad en nuestros sistemas o en software que hemos desarrollado, te animamos a comunicárnoslo de forma responsable.</p>
              </div>
            </div>
            <div className="pl-14 space-y-3">
              {reportSteps.map((step) => (
                <div key={step.step} className="flex items-start gap-4 bg-white/60 rounded-xl p-4 border border-slate-200/30">
                  <div className="shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-xs">
                    {step.step}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm mb-0.5">{step.title}</p>
                    <p className="text-slate-500 text-xs leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="pl-14 mt-4 p-4 rounded-xl bg-amber-50 border border-amber-100">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-amber-800 text-xs leading-relaxed">
                  <span className="font-semibold">Nota importante:</span> Por favor, no realice pruebas de penetración activas, no acceda a datos de otros usuarios y no realice acciones que puedan interrumpir el servicio. El reporte responsable es el único método aceptado.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Data retention */}
        <motion.div {...stagger(4)} className="mb-6">
          <div className="glass-card rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-50 to-sky-50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-cyan-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 leading-snug pt-1.5">Política de retención y borrado de datos</h2>
            </div>
            <div className="pl-14">
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                Mantenemos los datos únicamente durante el tiempo necesario para cumplir los fines para los que fueron recogidos y las obligaciones legales aplicables. Nuestra política de retención es la siguiente:
              </p>
              <div className="overflow-x-auto rounded-xl border border-slate-200/40">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/80">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 text-xs">Categoría de datos</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 text-xs">Período de retención</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 text-xs">Método de eliminación</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200/40">
                    {retentionPolicies.map((row, i) => (
                      <tr key={row.category} className={i % 2 === 0 ? 'bg-white/40' : 'bg-white/20'}>
                        <td className="px-4 py-3 text-slate-700 text-xs font-medium">{row.category}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{row.retention}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{row.deletion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-slate-500 text-xs mt-3 leading-relaxed">
                Los datos son eliminados de forma segura mediante procesos que garantizan su irrecuperabilidad. Puede solicitar la eliminación anticipada de sus datos contactando con nosotros en cualquier momento.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-4 glass-card rounded-2xl p-8 text-center"
        >
          <Mail className="w-8 h-8 text-slate-400 mx-auto mb-3" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">¿Ha encontrado un problema de seguridad?</h3>
          <p className="text-slate-500 text-sm mb-5 max-w-lg mx-auto">
            Contacte con nuestro equipo de forma responsable. Agradecemos todos los reportes que contribuyan a mejorar la seguridad de nuestros sistemas.
          </p>
          <a
            href="mailto:info@innovatheratech.com?subject=VULNERABILIDAD%20SEGURIDAD"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] active:scale-95"
          >
            <Mail className="w-4 h-4" />
            Reportar vulnerabilidad
          </a>
          <p className="text-xs text-slate-400 mt-3 font-mono tracking-wide">info@innovatheratech.com</p>
        </motion.div>

      </div>
    </section>
  )
}
